import type { ListShipment } from '@fv/client-types'
import type { AccountSettings, BatchDocumentType } from '@fv/models'

import type {
  BatchPrintDocumentOption,
  BatchPrintDocumentType,
  BatchPrintEligibilityShipment,
  BatchPrintSummary,
} from './types'

function getParcelLabelUrl({
  documents,
}: Pick<BatchPrintEligibilityShipment, 'documents'>) {
  const labelDoc = documents?.find(d => d.type === 'label-combined')
  return labelDoc?.url
}

function isBolProcessing(
  shipment?: Pick<BatchPrintEligibilityShipment, 'bol' | 'status'>,
): boolean {
  const { bol, status } = shipment ?? {}
  if (status === 'canceled') return false
  return bol?.status === 'pending' || bol?.status === 'requesting'
}

type LtlShippingSettings = AccountSettings['shipping']['ltl']
type TlShippingSettings = AccountSettings['shipping']['tl']

const isFreightWorkflow = (workflow: ListShipment['workflow']) =>
  workflow === 'ltl' || workflow === 'truckload'

function isWorkflowLabelSettingsReady(
  settings: Pick<
    LtlShippingSettings | TlShippingSettings,
    'preferredLabelCount' | 'preferredLabelSize' | 'rememberLabelSize'
  >,
): boolean {
  if (!settings.rememberLabelSize || !settings.preferredLabelCount) return false

  return true
}

export function isAccountFreightLabelGateOpen(
  shipping: AccountSettings['shipping'],
): boolean {
  const ltlReady = isWorkflowLabelSettingsReady(shipping.ltl)
  const tlReady = isWorkflowLabelSettingsReady(shipping.tl)
  return ltlReady || tlReady
}

export function isAccountParcelLabelGateOpen(
  shipping: AccountSettings['shipping'],
): boolean {
  return shipping.parcel?.preferredLabelType !== 'zpl'
}

export function canPrintParcelLabelsForLoad(
  load: BatchPrintEligibilityShipment,
): boolean {
  return load.workflow === 'parcel' && !!getParcelLabelUrl(load)
}

export function canPrintFreightLabelsForLoad(
  load: BatchPrintEligibilityShipment,
  shipping: AccountSettings['shipping'],
): boolean {
  if (!isFreightWorkflow(load.workflow)) return false
  if (load.status === 'canceled' || load.status === 'pending') return false
  if (load.bol?.status !== 'ok') return false

  return load.workflow === 'truckload'
    ? isWorkflowLabelSettingsReady(shipping.tl)
    : isWorkflowLabelSettingsReady(shipping.ltl)
}

export function isBolEligible(
  load: Pick<BatchPrintEligibilityShipment, 'documents'>,
): boolean {
  return load.documents?.some(d => d.type === 'bol') ?? false
}

export function canPrintBolForLoad(
  load: Pick<
    BatchPrintEligibilityShipment,
    'bol' | 'documents' | 'pickup' | 'status'
  >,
): boolean {
  if (load.status === 'canceled') return false
  if (isBolProcessing(load)) return false
  if (load.pickup?.status === 'error') return false
  return isBolEligible(load)
}

function countEligible(
  loads: BatchPrintEligibilityShipment[],
  predicate: (load: BatchPrintEligibilityShipment) => boolean,
): number {
  return loads.filter(predicate).length
}

export function computeBatchPrintSummary(
  loads: BatchPrintEligibilityShipment[],
  shipping: AccountSettings['shipping'],
): BatchPrintSummary {
  const parcelGateOpen = isAccountParcelLabelGateOpen(shipping)
  const freightGateOpen = isAccountFreightLabelGateOpen(shipping)

  const parcelEligible = countEligible(loads, canPrintParcelLabelsForLoad)
  const freightEligible = countEligible(loads, load =>
    canPrintFreightLabelsForLoad(load, shipping),
  )
  const bolEligible = countEligible(loads, canPrintBolForLoad)

  const documentOptions: BatchPrintDocumentOption[] = [
    {
      type: 'parcel-labels',
      label: 'Print parcel labels',
      eligibleCount: parcelEligible,
      disabledReason: !parcelGateOpen
        ? 'Parcel labels are unavailable when your preferred parcel label type is ZPL. Update paperwork settings to use PDF.'
        : parcelEligible === 0
          ? 'No selected shipments have parcel labels available.'
          : undefined,
    },
    {
      type: 'freight-labels',
      label: 'Print freight labels',
      eligibleCount: freightEligible,
      disabledReason: !freightGateOpen
        ? 'Set a default freight label size and enable skipping the label selection screen in paperwork settings.'
        : freightEligible === 0
          ? 'No selected shipments have freight labels available.'
          : undefined,
    },
    {
      type: 'bol',
      label: 'Print BOLs',
      eligibleCount: bolEligible,
      disabledReason:
        bolEligible === 0
          ? 'No selected shipments have a BOL available.'
          : undefined,
    },
  ]

  const needsFreightGate = loads.some(load => isFreightWorkflow(load.workflow))
  const needsParcelGate = loads.some(load => load.workflow === 'parcel')
  const accountGateBlocked =
    (needsFreightGate && !freightGateOpen) ||
    (needsParcelGate && !parcelGateOpen)

  let accountGateMessage: string | undefined
  if (accountGateBlocked) {
    const parts: string[] = []
    if (needsFreightGate && !freightGateOpen) {
      parts.push('enable default freight label size and count')
    }
    if (needsParcelGate && !parcelGateOpen) {
      parts.push('set parcel label type to PDF')
    }
    accountGateMessage = `Update paperwork settings to ${parts.join(' and ')}.`
  }

  return {
    documentOptions,
    accountGateBlocked,
    accountGateMessage,
  }
}

export function isDocumentTypeEnabled(
  option: BatchPrintDocumentOption,
): boolean {
  return option.eligibleCount > 0 && !option.disabledReason
}

export function getEligibleLoadIdsForType(
  loads: BatchPrintEligibilityShipment[],
  shipping: AccountSettings['shipping'],
  type: BatchPrintDocumentType,
): string[] {
  return loads
    .filter(load => {
      switch (type) {
        case 'parcel-labels':
          return canPrintParcelLabelsForLoad(load)
        case 'freight-labels':
          return canPrintFreightLabelsForLoad(load, shipping)
        case 'bol':
          return canPrintBolForLoad(load)
      }
    })
    .map(load => load.loadId)
}

export function toBatchDocumentType(
  type: BatchPrintDocumentType,
): BatchDocumentType {
  return type === 'bol' ? 'bol' : 'labels'
}
